import { useState, useEffect, useCallback } from "react";
import { api } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { Route, Loader2, Info } from "lucide-react";

const MILESTONES = ["Justificación", "Adjudicación", "Presentación", "En curso", "Finalizado", "Descartado"];

const MILESTONE_COLOR = {
  "Justificación": "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  "Adjudicación": "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
  "Presentación": "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  "En curso": "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  "Finalizado": "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
  "Descartado": "bg-red-100 text-red-600 dark:bg-red-950/40 dark:text-red-400 line-through",
};

export function RdPipelineCard({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = useCallback(async () => {
    try {
      const data = await api.getRdPipeline(token);
      setItems(data);
    } catch {
      toast.error("Error al cargar el pipeline de I+D");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleMilestoneChange = async (id, milestone) => {
    try {
      await api.updateRdPipelineItem(token, id, { current_milestone: milestone, status: milestone === "Descartado" ? "Descartado" : "Activo" });
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, current_milestone: milestone, status: milestone === "Descartado" ? "Descartado" : "Activo" } : i)));
    } catch {
      toast.error("Error al actualizar el hito");
    }
  };

  const activos = items.filter((i) => i.status !== "Descartado").length;

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (items.length === 0) return null;

  return (
    <Card data-testid="rd-pipeline-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Route className="w-4 h-4 text-emerald-500" />
          Pipeline I+D 2026-2029
          <Badge variant="outline" className="ml-auto font-mono">{activos} activos</Badge>
        </CardTitle>
        <div className="flex items-start gap-1.5 text-xs text-muted-foreground pt-1">
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>Hitos leídos del Gantt de seguimiento. Las fechas son aproximadas — ajusta el hito si lo necesitas.</span>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.map((it) => (
          <div key={it.id} className="flex items-center gap-3 py-1.5 border-b last:border-0" data-testid={`rd-pipeline-row-${it.id}`}>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium truncate ${it.status === "Descartado" ? "line-through text-muted-foreground" : ""}`}>{it.name}</p>
              <p className="text-xs text-muted-foreground">{it.estimated_period}</p>
            </div>
            <Select value={it.current_milestone} onValueChange={(v) => handleMilestoneChange(it.id, v)}>
              <SelectTrigger className={`h-7 text-xs w-[150px] border-0 ${MILESTONE_COLOR[it.current_milestone] || ""}`} data-testid={`rd-pipeline-milestone-${it.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MILESTONES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
